import {
    RecurringSeriesViewState,
    RecurringSeriesViewStatus,
    fetchRecurringSeriesThunk,
    resetRecurringSeriesViewThunk
} from '@spike/new-bookings-action';
import { showSuccess } from '@spike/notifications-action';
import { useApiClientWrapper } from 'hooks';
import { SeriesCreationPhase, getSeriesCreationPhase } from 'model/RecurringSeries';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import useRecurringSeriesChannel from 'WebSockets/useRecurringSeriesChannel';
import { SERIES_PAGE_SIZE } from './View/useRecurringSeriesView';

const FALLBACK_POLL_INTERVAL_MS = 10000;
const RETRY_INTERVAL_MS = 2000;
const MAX_CONSECUTIVE_FAILURES = 3;
const SERIES_CREATED_MESSAGE = 'Recurrent Appointments Created';

interface SeriesCreationProgress {
    uuid: string;
    phase: SeriesCreationPhase;
}

export const useRecurringSeriesCreationProgress = (
    uuid: string | undefined,
    initialStatus: string | undefined
): SeriesCreationPhase | undefined => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const view = useSelector<RootState, RecurringSeriesViewState>(state => state.newBookings.recurringSeriesView);
    const [progress, setProgress] = useState<SeriesCreationProgress | undefined>(undefined);
    const awaitingResult = useRef(false);
    const requestStarted = useRef(false);
    const refetchPending = useRef(false);
    const consecutiveFailures = useRef(0);
    const pollTimeout = useRef<number>();

    const poll = useCallback(
        () => {
            if (!uuid) {
                return;
            }

            window.clearTimeout(pollTimeout.current);
            awaitingResult.current = true;
            requestStarted.current = false;
            refetchPending.current = false;
            dispatch(fetchRecurringSeriesThunk(apiClientWrapper, uuid, 1, SERIES_PAGE_SIZE));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, uuid]
    );

    const scheduleNextPoll = useCallback(
        (delay: number) => {
            window.clearTimeout(pollTimeout.current);
            pollTimeout.current = window.setTimeout(poll, delay);
        },
        [poll]
    );

    const seriesFinishedHandler = useCallback(() => {
        if (awaitingResult.current) {
            refetchPending.current = true;
            return;
        }
        poll();
    }, [poll]);

    const seriesProgressHandler = useCallback(() => {
        if (!awaitingResult.current) {
            scheduleNextPoll(FALLBACK_POLL_INTERVAL_MS);
        }
    }, [scheduleNextPoll]);

    const phase =
        uuid && initialStatus !== undefined
            ? progress?.uuid === uuid
                ? progress.phase
                : getSeriesCreationPhase(initialStatus)
            : undefined;

    useRecurringSeriesChannel(phase === 'creating' ? uuid : undefined, seriesFinishedHandler, seriesProgressHandler);

    useEffect(
        () => {
            consecutiveFailures.current = 0;

            if (!uuid || initialStatus === undefined) {
                return;
            }

            dispatch(resetRecurringSeriesViewThunk());

            const initialPhase = getSeriesCreationPhase(initialStatus);
            setProgress({ uuid, phase: initialPhase });
            if (initialPhase === 'creating') {
                poll();
            } else if (initialPhase === 'created') {
                dispatch(showSuccess(SERIES_CREATED_MESSAGE));
            }

            return () => {
                window.clearTimeout(pollTimeout.current);
                awaitingResult.current = false;
                refetchPending.current = false;
                dispatch(resetRecurringSeriesViewThunk());
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [uuid]
    );

    useEffect(() => {
        if (!awaitingResult.current) {
            return;
        }

        if (view.status === RecurringSeriesViewStatus.Fetching) {
            requestStarted.current = true;
            return;
        }

        if (!requestStarted.current) {
            return;
        }

        if (view.status === RecurringSeriesViewStatus.FetchSuccess && view.detail) {
            awaitingResult.current = false;
            consecutiveFailures.current = 0;

            const nextPhase = getSeriesCreationPhase(view.detail.status);
            setProgress({ uuid: view.detail.uuid, phase: nextPhase });
            if (nextPhase === 'creating') {
                if (refetchPending.current) {
                    poll();
                } else {
                    scheduleNextPoll(FALLBACK_POLL_INTERVAL_MS);
                }
            } else if (nextPhase === 'created') {
                dispatch(showSuccess(SERIES_CREATED_MESSAGE));
            }
            return;
        }

        if (view.status === RecurringSeriesViewStatus.FetchError) {
            awaitingResult.current = false;
            consecutiveFailures.current += 1;

            if (consecutiveFailures.current >= MAX_CONSECUTIVE_FAILURES) {
                setProgress(uuid ? { uuid, phase: 'failed' } : undefined);
            } else {
                scheduleNextPoll(RETRY_INTERVAL_MS);
            }
        }
    }, [view.status, view.detail, uuid, poll, scheduleNextPoll]);

    return phase;
};

export default useRecurringSeriesCreationProgress;
